import { enqueueActivityEvent, isActivityQueueEnabled } from 'services/activityTracking/activityEventQueue';
import { getProducerSessionId, nextEventId, nextProducerSequence } from 'services/activityTracking/session';
import { FACE_MATCH_NO_FACE_MESSAGE, getConfidenceLevel, getFaceMatchFailureMessage } from 'store/faceMatchSlice/faceMatchSlice';
import {
    ACTIVITY_EVENT_TYPE,
    ACTIVITY_FACE_MATCH_ERROR_TYPE,
    ACTIVITY_FACE_MATCH_STATUS,
    ACTIVITY_FACE_MATCH_TRIGGER,
} from 'utils/activityTracking/constants';
import { buildActivityEvent } from 'utils/activityTracking/envelope';
import {
    toActivityCameraPair,
    toActivityConfidenceBand,
    toActivityConfidenceScore,
    toActivityErrorMessage,
    toActivityFaceMatchErrorType,
    toActivityFaceMatchInputSource,
} from 'utils/activityTracking/faceMatch';
import { FACE_MATCH_FAILURE } from 'utils/faceMatchEnforcement';

/**
 * `face_match.requested` / `face_match.responded` (BEAST-13916).
 *
 * The browser calls AWS Rekognition itself, so the backend never sees an attempt: the attempt number and the
 * score can only come from here. One request actually sent is one attempt, paired with its response by
 * `correlation_id`. A capture failure sends no request, so it is neither an attempt nor a response.
 */

// Counted whether tracking is on or not, so turning it on mid-transaction keeps the numbering right. Kept for
// the page's life so a reopened transaction continues its count: a few hundred numbers at most.
const attemptCounts = new Map();

// The tracked attempt still waiting for its result, per transaction. Responses only ever close one of these,
// which is what makes the tracking decision taken at request time also cover the response.
const openAttempts = new Map();

// The transaction the newest request is for. The face match saga is a global takeLatest, so a cancellation for
// this same transaction is a retry whose new attempt is already open, not the end of it.
let latestRequestTransactionId = null;

const toWorkObjectId = (transactionId) =>
    transactionId === null || transactionId === undefined || transactionId === '' ? null : String(transactionId);

function normalizeContext(context) {
    return {
        locationId: context?.locationId ?? null,
        gateId: context?.gateId ?? null,
        deskId: context?.deskId ?? null,
    };
}

function emit(eventType, attempt, faceMatch) {
    if (!isActivityQueueEnabled()) return;
    enqueueActivityEvent(
        buildActivityEvent({
            eventId: nextEventId(),
            eventType,
            occurredAt: new Date().toISOString(),
            producerSessionId: getProducerSessionId(),
            producerSequence: nextProducerSequence(),
            workObjectId: attempt.workObjectId,
            targetId: attempt.workObjectId,
            correlationId: attempt.correlationId,
            faceMatch: { attemptNumber: attempt.attemptNumber, cameraPair: attempt.cameraPair, ...faceMatch },
            ...attempt.context,
        })
    );
}

function takeOpenAttempt(transactionId) {
    const workObjectId = toWorkObjectId(transactionId);
    const attempt = workObjectId ? openAttempts.get(workObjectId) : null;
    if (attempt) openAttempts.delete(workObjectId);
    return attempt ?? null;
}

export function recordFaceMatchRequested({ transactionId, cameraPair, imageSource, context } = {}) {
    try {
        const workObjectId = toWorkObjectId(transactionId);
        if (!workObjectId) return;

        const attemptNumber = (attemptCounts.get(workObjectId) ?? 0) + 1;
        attemptCounts.set(workObjectId, attemptNumber);
        latestRequestTransactionId = workObjectId;
        // A retry before the result: the cancelled request never answers, so its attempt stays unanswered.
        openAttempts.delete(workObjectId);

        const wireCameraPair = toActivityCameraPair(cameraPair);
        const inputSource = toActivityFaceMatchInputSource(imageSource);
        if (!isActivityQueueEnabled() || !wireCameraPair || !inputSource) return;

        const attempt = { correlationId: nextEventId(), attemptNumber, cameraPair: wireCameraPair, workObjectId, context: normalizeContext(context) };
        openAttempts.set(workObjectId, attempt);
        emit(ACTIVITY_EVENT_TYPE.FACE_MATCH_REQUESTED, attempt, { inputSource, trigger: ACTIVITY_FACE_MATCH_TRIGGER.MANUAL });
    } catch (error) {
        console.warn('activityTracking: face match request event failed.', error);
    }
}

export function recordFaceMatchSucceeded({ transactionId, similarity } = {}) {
    try {
        const attempt = takeOpenAttempt(transactionId);
        if (!attempt) return;

        const confidenceScore = toActivityConfidenceScore(similarity);
        // The service answered but found no face to compare: the agent saw an error, not a score.
        if (confidenceScore === null) {
            emit(ACTIVITY_EVENT_TYPE.FACE_MATCH_RESPONDED, attempt, {
                status: ACTIVITY_FACE_MATCH_STATUS.ERROR,
                errorType: ACTIVITY_FACE_MATCH_ERROR_TYPE.FACE_NOT_DETECTED,
                errorMessage: FACE_MATCH_NO_FACE_MESSAGE,
            });
            return;
        }

        emit(ACTIVITY_EVENT_TYPE.FACE_MATCH_RESPONDED, attempt, {
            status: ACTIVITY_FACE_MATCH_STATUS.SUCCESS,
            confidenceScore,
            confidenceBand: toActivityConfidenceBand(getConfidenceLevel(similarity).level),
        });
    } catch (error) {
        console.warn('activityTracking: face match response event failed.', error);
    }
}

export function recordFaceMatchFailed({ transactionId, error } = {}) {
    try {
        if (error?.code === FACE_MATCH_FAILURE.SNAPSHOT_ERROR) return;
        const attempt = takeOpenAttempt(transactionId);
        if (!attempt) return;

        emit(ACTIVITY_EVENT_TYPE.FACE_MATCH_RESPONDED, attempt, {
            status: ACTIVITY_FACE_MATCH_STATUS.ERROR,
            errorType: toActivityFaceMatchErrorType(error?.code),
            errorMessage: toActivityErrorMessage(getFaceMatchFailureMessage(error)),
        });
    } catch (trackingError) {
        console.warn('activityTracking: face match response event failed.', trackingError);
    }
}

// A face match on another transaction cancelled this one's request: it will never answer.
export function dropFaceMatchAttempt(transactionId) {
    const workObjectId = toWorkObjectId(transactionId);
    if (!workObjectId || workObjectId === latestRequestTransactionId) return;
    openAttempts.delete(workObjectId);
}

/** Test seam only. */
export function resetFaceMatchTracker() {
    attemptCounts.clear();
    openAttempts.clear();
    latestRequestTransactionId = null;
}
